import {
  Cancel as CancelIcon,
  Check as CheckIcon,
  CheckCircle as CheckCircleIcon,
  Clear as ClearIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Remove as RemoveIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  Chip,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  AdminEmptyState,
  PageShell,
  AdminTableActions,
  AdminTableBodyState,
  AdminTablePanel,
} from "@/components/common";
import { SortableTableCell } from "@/components/common/SortableTableCell";
import { useLocalizedTablePagination } from "@/hooks/useLocalizedTablePagination";
import { useTableSorting } from "@/hooks/useTableSorting";
import {
  Customer,
  CustomerConfig,
  CustomerService,
  StoreEnablement,
} from "@/services/features/CustomerService";

type CustomerConfigSortColumn = "id" | "customer" | "active";

const Configs: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const customerService = CustomerService.getInstance();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const localizedTablePagination = useLocalizedTablePagination();

  const [configs, setConfigs] = useState<CustomerConfig[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const customerActivityById = useMemo(
    () => new Map(customers.map((customer) => [customer.id, customer.active])),
    [customers],
  );
  const filteredConfigs = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    return configs.filter((config) => {
      const isCustomerActive = customerActivityById.get(config.customerId) ?? true;
      const matchesSearch =
        !normalizedQuery ||
        config.customerName.toLowerCase().includes(normalizedQuery) ||
        config.customerId.toString().includes(normalizedQuery);

      return (showInactive || isCustomerActive) && matchesSearch;
    });
  }, [configs, customerActivityById, searchQuery, showInactive]);
  const { sortColumn, sortDirection, handleSort, sortedItems } = useTableSorting<
    CustomerConfig,
    CustomerConfigSortColumn
  >(
    filteredConfigs,
    (config, column) => {
      switch (column) {
        case "id":
          return config.customerId;
        case "customer":
          return config.customerName;
        case "active":
          return customerActivityById.get(config.customerId) ?? true;
      }
    },
    "customer",
  );
  const paginatedConfigs = useMemo(
    () => sortedItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [page, rowsPerPage, sortedItems],
  );

  const getConfiguredApps = (config: CustomerConfig) => [
    ...(config.shopperApp
      ? [
          {
            key: "shopper",
            label: t("operations.configs.configs.apps.shopper"),
            iconSrc: "/images/app-icons/shopper_assist.svg",
          },
        ]
      : []),
    ...(config.assistApp
      ? [
          {
            key: "digitalAssistant",
            label: t("operations.configs.configs.apps.digitalAssistant"),
            iconSrc: "/images/app-icons/shopper_assist.svg",
          },
        ]
      : []),
    ...(config.associateConfig
      ? [
          {
            key: "associate",
            label: t("operations.configs.configs.apps.associate"),
            iconSrc: "/images/app-icons/associate.svg",
          },
        ]
      : []),
  ];

  const getV2Status = (enablement?: StoreEnablement) => {
    if (enablement?.enabledForAll) {
      return "enabled";
    }

    if (enablement?.enabledOnStore?.length) {
      return "partial";
    }

    return "disabled";
  };

  const getV2StatusChips = (config: CustomerConfig) => {
    const designStatus = getV2Status(config.generalConfig?.v2DesignEnabled);
    const loggerStatus = getV2Status(config.generalConfig?.v2LoggerEnabled);

    return [
      {
        key: "design",
        label: t("operations.configs.configs.status.newDesign"),
        color:
          designStatus === "enabled" ? "success" : designStatus === "partial" ? "warning" : "error",
        icon:
          designStatus === "enabled" ? (
            <CheckIcon />
          ) : designStatus === "partial" ? (
            <RemoveIcon />
          ) : (
            <CloseIcon />
          ),
      },
      {
        key: "logger",
        label: t("operations.configs.configs.status.newLogger"),
        color:
          loggerStatus === "enabled" ? "success" : loggerStatus === "partial" ? "warning" : "error",
        icon:
          loggerStatus === "enabled" ? (
            <CheckIcon />
          ) : loggerStatus === "partial" ? (
            <RemoveIcon />
          ) : (
            <CloseIcon />
          ),
      },
    ] as const;
  };

  const renderActiveIcon = (isActive: boolean) => (
    <Tooltip
      title={
        isActive
          ? t("management.customers.customers.fields.active")
          : t("commandCenter.status.inactive")
      }
    >
      <Box component="span" sx={{ display: "inline-flex" }}>
        {isActive ? (
          <CheckCircleIcon color="success" sx={{ fontSize: 18 }} />
        ) : (
          <CancelIcon color="error" sx={{ fontSize: 18 }} />
        )}
      </Box>
    </Tooltip>
  );

  const loadConfigs = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [customersResponse, configsResponse] = await Promise.all([
        customerService.getAllCustomers({ includeInactive: true }),
        customerService.getAllCustomerConfigs(),
      ]);

      if (!customersResponse.success || !customersResponse.data || !configsResponse.success) {
        throw new Error(
          customersResponse.error ||
            configsResponse.error ||
            t("operations.configs.configs.errors.failedLoadConfigurations"),
        );
      }

      setCustomers(customersResponse.data);
      setConfigs(configsResponse.data ?? []);
      setPage(0);
    } catch (loadError) {
      const errorMessage =
        loadError instanceof Error
          ? loadError.message
          : t("operations.configs.configs.errors.failedLoadConfigurations");
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    document.title = t("operations.configs.configs.documentTitle");
  }, [t]);

  useEffect(() => {
    void loadConfigs();
    // This list is loaded once; customer activity and config data are not locale-dependent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEditConfig = (customerId: number) => {
    void navigate(`/admin/configuration/configs/edit/${customerId}`);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleShowInactiveChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowInactive(event.target.checked);
    setPage(0);
  };

  const handleTableSort = (column: CustomerConfigSortColumn) => {
    handleSort(column);
    setPage(0);
  };

  const renderPagination = () => (
    <TablePagination
      component="div"
      count={sortedItems.length}
      page={page}
      onPageChange={(_event, nextPage) => setPage(nextPage)}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={(event) => {
        setRowsPerPage(Number(event.target.value));
        setPage(0);
      }}
      rowsPerPageOptions={isMobile ? [] : [25, 50, 100, 250]}
      {...localizedTablePagination}
    />
  );

  return (
    <PageShell
      breadcrumbs="admin"
      title={t("operations.configs.configs.title")}
      icon={<SettingsIcon />}
    >
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            "& .MuiAlert-message": { alignItems: "center", display: "flex" },
          }}
        >
          {error}
        </Alert>
      )}

      <AdminTablePanel
        header={
          <Stack spacing={2}>
            <TextField
              fullWidth
              placeholder={t("operations.configs.configs.placeholders.searchConfigurations")}
              value={searchQuery}
              onChange={handleSearchChange}
              inputProps={{
                "aria-label": t("operations.configs.configs.placeholders.searchConfigurations"),
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchQuery("");
                        setPage(0);
                      }}
                      edge="end"
                      sx={{ color: "text.disabled", "&:hover": { color: "text.primary" } }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
            />
            <Box
              sx={{
                alignItems: { sm: "center", xs: "stretch" },
                display: "flex",
                flexDirection: { sm: "row", xs: "column" },
                gap: 1,
                justifyContent: "space-between",
              }}
            >
              <FormControlLabel
                control={<Switch checked={showInactive} onChange={handleShowInactiveChange} />}
                label={t("management.customers.customers.actions.showInactive")}
                sx={{ m: 0 }}
              />
              <Box
                sx={{
                  alignSelf: { sm: "auto", xs: "flex-end" },
                  "& .MuiTablePagination-root": { p: 0 },
                }}
              >
                {renderPagination()}
              </Box>
            </Box>
          </Stack>
        }
        footer={
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>{renderPagination()}</Box>
        }
        containerProps={{ sx: { overflowX: "auto" } }}
        tableProps={{ size: isMobile ? "small" : "medium" }}
        tableSx={{
          minWidth: 720,
          "& .MuiTableHead-root .MuiTableCell-root": {
            borderBottom: (tableTheme) => `1px solid ${tableTheme.palette.divider}`,
            borderTop: (tableTheme) => `1px solid ${tableTheme.palette.divider}`,
          },
          "& .MuiTableHead-root .MuiTableCell-root:first-of-type": {
            borderInlineStart: (tableTheme) => `1px solid ${tableTheme.palette.divider}`,
            borderStartStartRadius: 0,
          },
          "& .MuiTableHead-root .MuiTableCell-root:last-of-type": {
            borderInlineEnd: (tableTheme) => `1px solid ${tableTheme.palette.divider}`,
            borderStartEndRadius: 0,
          },
        }}
      >
        <TableHead>
          <TableRow>
            <SortableTableCell
              sx={{ fontWeight: 600, width: "8%" }}
              activeColumn={sortColumn}
              column="id"
              direction={sortDirection}
              onSort={handleTableSort}
            >
              {t("operations.configs.configs.fields.id")}
            </SortableTableCell>
            <SortableTableCell
              sx={{ fontWeight: 600, width: "15%" }}
              activeColumn={sortColumn}
              column="customer"
              direction={sortDirection}
              onSort={handleTableSort}
            >
              {t("common.customer")}
            </SortableTableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {t("operations.configs.configs.fields.apps")}
            </TableCell>
            <TableCell sx={{ fontWeight: 600, width: "18%" }}>
              {t("operations.configs.configs.fields.v2Status")}
            </TableCell>
            {showInactive ? (
              <SortableTableCell
                align="center"
                activeColumn={sortColumn}
                column="active"
                direction={sortDirection}
                onSort={handleTableSort}
                sx={{
                  fontWeight: 600,
                  maxWidth: 100,
                  minWidth: 100,
                  whiteSpace: "nowrap",
                  width: 100,
                }}
              >
                {t("management.customers.customers.fields.active")}
              </SortableTableCell>
            ) : null}
            <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: "nowrap", width: "1%" }}>
              {t("operations.configs.configs.fields.actions")}
            </TableCell>
          </TableRow>
        </TableHead>
        <AdminTableBodyState
          items={paginatedConfigs}
          isLoading={isLoading}
          colSpan={showInactive ? 6 : 5}
          loadingLabel={t("common.loading")}
          emptyState={
            <AdminEmptyState
              icon={<SettingsIcon sx={{ fontSize: 40 }} />}
              text={
                searchQuery || showInactive
                  ? t("operations.configs.configs.empty.noMatchingConfigurationsFound")
                  : t("operations.configs.configs.empty.noConfigurationsAvailable")
              }
            />
          }
        >
          {(config) => (
            <TableRow
              key={config.customerId}
              hover
              tabIndex={0}
              role="button"
              aria-label={t("operations.configs.configs.actions.editConfiguration")}
              onClick={() => handleEditConfig(config.customerId)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  handleEditConfig(config.customerId);
                }
              }}
              sx={{
                cursor: "pointer",
                "&:focus-visible": {
                  outline: "2px solid",
                  outlineColor: "primary.main",
                  outlineOffset: -2,
                },
              }}
            >
              <TableCell sx={{ color: "text.secondary", verticalAlign: "middle" }}>
                {config.customerId}
              </TableCell>
              <TableCell sx={{ verticalAlign: "middle" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  {config.generalConfig?.avatar && (
                    <Avatar
                      alt=""
                      src={config.generalConfig.avatar}
                      variant="circular"
                      sx={{
                        width: 40,
                        height: 40,
                        outline: "1px solid #000",
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {config.customerName}
                    </Typography>
                    {config.generalConfig?.customerEnum && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block" }}
                      >
                        {config.generalConfig.customerEnum}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </TableCell>
              <TableCell sx={{ verticalAlign: "top" }}>
                <Stack direction="column" spacing={0.75} alignItems="flex-start">
                  {getConfiguredApps(config).map((app) => (
                    <Chip
                      key={app.key}
                      avatar={<Avatar alt="" src={app.iconSrc} sx={{ height: 18, width: 18 }} />}
                      label={app.label}
                      size="small"
                    />
                  ))}
                </Stack>
              </TableCell>
              <TableCell sx={{ verticalAlign: "top" }}>
                <Stack direction="column" spacing={0.75} alignItems="flex-start">
                  {getV2StatusChips(config).map((status) => (
                    <Chip
                      key={status.key}
                      color={status.color}
                      icon={status.icon}
                      label={status.label}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Stack>
              </TableCell>
              {showInactive && (
                <TableCell align="center" sx={{ maxWidth: 100, minWidth: 100, width: 100 }}>
                  {renderActiveIcon(customerActivityById.get(config.customerId) ?? true)}
                </TableCell>
              )}
              <TableCell align="right" sx={{ whiteSpace: "nowrap", width: "1%" }}>
                <AdminTableActions
                  actions={[
                    {
                      icon: <EditIcon />,
                      hint: t("operations.configs.configs.tooltips.editConfiguration"),
                      action: () => handleEditConfig(config.customerId),
                    },
                  ]}
                />
              </TableCell>
            </TableRow>
          )}
        </AdminTableBodyState>
      </AdminTablePanel>
    </PageShell>
  );
};

export default Configs;
